import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  firestore,
  writeBatch,
  collection,
  doc,
  query,
  where,
  getDocs
} from '../config/firebaseConfig';
import { Timestamp } from 'firebase/firestore'; // Importando Timestamp diretamente de firebase/firestore
import './ImportPillsModal.css';

function ImportPillsModal({ onClose, currentUser, onImportComplete }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [ignoredRows, setIgnoredRows] = useState([]);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const tenantId = localStorage.getItem('tenantId') || 'unknownTenant'; // Pegando tenantId do localStorage

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const getPillDoc = async (titulo, temasIds) => {
    const pillsCollectionRef = collection(firestore, 'pilulas');
    const q = query(
      pillsCollectionRef,
      where('titulo', '==', titulo),
      where('temas', 'array-contains-any', temasIds),
      where('tenantId', '==', tenantId) // Filtrando pelo tenantId
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.empty ? null : querySnapshot.docs[0];
  };

  const mapThemesToIds = async (temas) => {
    const temasCollectionRef = collection(firestore, 'temas');
    const q = query(temasCollectionRef, where('nome', 'in', temas), where('tenantId', '==', tenantId)); // Filtrando pelo tenantId
    const querySnapshot = await getDocs(q);
    const temasMap = {};
    querySnapshot.docs.forEach(doc => {
      temasMap[doc.data().nome] = doc.id;
    });
    return temas.map(nome => temasMap[nome] || null).filter(id => id !== null);
  };

  const convertExcelDate = (excelDate) => {
    if (typeof excelDate === 'number') {
      const days = Math.floor(excelDate - 25569);
      const date = new Date(days * 86400 * 1000);
      return date.toISOString().split('T')[0];
    }
    return null;
  };

  const handleImport = async () => {
    if (!file) {
      setError('Por favor, selecione um arquivo.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress(0);
    setIgnoredRows([]);
    setProcessedCount(0);
    setTotalRecords(0);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'binary' });
        const sheetName = workbook.SheetNames.find(name => name === 'Pilulas');

        if (!sheetName) {
          setError('A aba "Pilulas" não encontrada no arquivo.');
          setLoading(false);
          return;
        }

        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (!data.length) {
          setError('O arquivo está vazio ou não possui dados válidos.');
          setLoading(false);
          return;
        }

        const batch = writeBatch(firestore);
        const pillsCollectionRef = collection(firestore, 'pilulas');
        const user = currentUser ? currentUser.email : 'unknown';
        const newIgnoredRows = [];

        setTotalRecords(data.length);

        for (let i = 0; i < data.length; i++) {
          const item = data[i];
          const { titulo, conteudo, temas, tipo, dataexpiracao } = item;

          if (!titulo || !conteudo || !temas || !tipo) {
            newIgnoredRows.push(i + 1);
            continue;
          }

          const temasArray = Array.isArray(temas) ? temas : [temas];
          const temasIds = await mapThemesToIds(temasArray);

          if (temasIds.length === 0) {
            newIgnoredRows.push(i + 1);
            continue;
          }

          let formattedDataexpiracao = convertExcelDate(dataexpiracao);

          const existingPillDoc = await getPillDoc(titulo, temasIds);
          let itemRef;

          if (existingPillDoc) {
            itemRef = existingPillDoc.ref;
            batch.update(itemRef, {
              conteudo,
              temas: temasIds,
              tipo,
              dataexpiracao: formattedDataexpiracao,
              updatedAt: Timestamp.now(),
              usuarioAlteracao: user,
              tenantId // Adicionando tenantId
            });
          } else {
            const idNumerico = Date.now().toString();  // Gerando ID numérico
            itemRef = doc(pillsCollectionRef, idNumerico);
            batch.set(itemRef, {
              titulo,
              conteudo,
              temas: temasIds,
              tipo,
              dataexpiracao: formattedDataexpiracao,
              createdAt: Timestamp.now(),  // Usando Timestamp do Firestore
              updatedAt: Timestamp.now(),  // Usando Timestamp do Firestore
              enviado: false,
              bloqueado: false,
              usuarioAlteracao: user,
              tenantId // Adicionando tenantId
            });
          }

          setProcessedCount(prevCount => prevCount + 1);
          setProgress(((i + 1) / data.length) * 100);
        }

        await batch.commit();
        setSuccess('Pílulas importadas e atualizadas com sucesso!');

        if (newIgnoredRows.length > 0) {
          setIgnoredRows(newIgnoredRows);
          if (ignoredRows.length > 0) {
            setError(`As seguintes linhas foram ignoradas: ${newIgnoredRows.join(', ')}`);
          }
        }
      } catch (error) {
        setError('Erro ao importar pílulas. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
        if (typeof onImportComplete === 'function') {
          onImportComplete();
        }
      }
    };

    reader.readAsBinaryString(file);
  };

  return (
    <div className="import-pills-modal">
      <div className="import-pills-modal-content">
        <span className="import-pills-modal-close" onClick={onClose}>&times;</span>
        <h3>Importar Pílulas</h3>
        <input
          type="file"
          accept=".xlsx"
          onChange={handleFileChange}
        />
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        {loading && (
          <div className="progress-container">
            <div className="progress-bar">
              <div className="progress-bar-filled" style={{ width: `${progress}%` }}>
                {Math.round(progress)}%
              </div>
            </div>
            <div className="processed-count">
              {processedCount} de {totalRecords} registros processados
            </div>
            {ignoredRows.length > 0 && (
              <div className="ignored-rows">
                Linhas ignoradas: {ignoredRows.join(', ')}
              </div>
            )}
          </div>
        )}
        <button onClick={handleImport} disabled={loading}>
          {loading ? 'Importando...' : 'Importar'}
        </button>
        <button onClick={onClose}>Fechar</button>
      </div>
    </div>
  );
}

export default ImportPillsModal;
